import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Sparkles,
  Star,
  Users,
} from "lucide-react";

import client from "@/api/client";
import { createCheckout, createPortal, getSubscription, listPlans } from "@/api/billing";
import type { PlanInfo, SubscriptionState } from "@/api/billing";
import { AiCreditsSection } from "@/pages/settings/AiCreditsSection";
import { getCurrentOrg } from "@/api/organizations";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { Skeleton } from "@/components/ui/Skeleton";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";

function formatSeatLimit(limit: number): string {
  // ``0`` is the canonical "unlimited" value on the wire. Treat any
  // implausibly-large number (>= 100k) as unlimited too — defends
  // against pre-deploy frontend bundles still seeing the internal
  // 999_999 sentinel before the API normalization shipped.
  if (limit === 0 || limit >= 100_000) return "Unlimited employees";
  return `Up to ${limit} employees`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function statusBadgeVariant(status: string | null): "success" | "warning" | "danger" | "default" {
  if (!status) return "default";
  const s = status.toLowerCase();
  if (s === "active" || s === "trialing") return "success";
  if (s === "past_due" || s === "incomplete" || s === "trial_expired") return "warning";
  if (s === "canceled" || s === "unpaid" || s === "incomplete_expired") return "danger";
  return "default";
}

function statusLabel(status: string | null): string {
  if (!status) return "free";
  if (status === "trial_expired") return "Trial expired";
  if (status === "trialing") return "Trial";
  return status;
}

interface PlanCardProps {
  plan: PlanInfo;
  current: boolean;
  canManage: boolean;
  disabled: boolean;
  onSelect: (plan: PlanInfo) => void;
  onContactSales: (plan: PlanInfo) => void;
  working: boolean;
  recommended?: boolean;
}

function PlanCard({
  plan,
  current,
  canManage,
  disabled,
  onSelect,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onContactSales: _onContactSales,
  working,
  recommended = false,
}: PlanCardProps) {
  const isFree = plan.key.toLowerCase() === "free";
  // Plans without a Stripe price ID are "contact-sales" tiers (Enterprise
  // today, potentially others). Clicking Upgrade on these would hit the
  // backend and raise BillingError("… has no Stripe price configured"),
  // which previously surfaced as the generic "Could not start checkout"
  // toast. Render a mailto CTA instead so the button does something real.
  const isContactSales = !isFree && !plan.purchasable;

  return (
    <Card
      className={cn(
        "flex flex-col gap-4 transition-colors",
        current
          ? "border-accent ring-2 ring-accent/30"
          : recommended
            ? "border-warning ring-2 ring-warning/30"
            : "hover:border-border/80"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h4 className="text-base font-semibold font-heading text-text-primary">
            {plan.name}
          </h4>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-text-muted">
            <Users className="h-3.5 w-3.5" />
            {formatSeatLimit(plan.seat_limit)}
          </p>
        </div>
        {current && <Badge variant="success">Current plan</Badge>}
        {!current && recommended && (
          <Badge variant="warning" className="flex items-center gap-1">
            <Star className="h-3 w-3" />
            Recommended
          </Badge>
        )}
      </div>

      {/* Founding-rate price block. Renders only when the plan
          carries a per-seat price AND a strictly-higher standard
          rate — i.e. the post-launch rack rate the customer is
          AVOIDING by joining now. Hidden on Enterprise / Free /
          contact-sales tiers (those have null per_seat). */}
      {plan.per_seat_price_usd != null &&
        plan.per_seat_price_usd > 0 && (
          <div className="rounded-xl border border-border/50 bg-surface-muted/40 px-4 py-3">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold tracking-tight text-text-primary">
                ${plan.per_seat_price_usd.toFixed(0)}
              </span>
              <span className="text-sm text-text-muted">
                / employee / month
              </span>
            </div>
            {plan.standard_per_seat_price_usd != null &&
              plan.standard_per_seat_price_usd >
                plan.per_seat_price_usd && (
                <div className="mt-1 flex items-center gap-2 text-xs">
                  <span className="font-semibold uppercase tracking-wider text-accent">
                    Founding rate
                  </span>
                  <span className="text-text-muted">
                    Standard{" "}
                    <span className="line-through">
                      ${plan.standard_per_seat_price_usd.toFixed(0)}
                    </span>
                  </span>
                </div>
              )}
          </div>
        )}

      <ul className="space-y-2 text-sm text-text-primary">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-2">
        {isFree ? (
          <Button
            variant="secondary"
            size="md"
            disabled
            className="w-full"
          >
            Included
          </Button>
        ) : isContactSales ? (
          // Enterprise-style contact-sales CTA. Renders as an
          // ``<a href="mailto:…">`` so a click opens the user's mail
          // client prefilled with "Enterprise inquiry" subject and
          // hello@lenavio.com to-line. Right-click → copy address still
          // works for users who don't have a default mail client wired
          // up. The in-app modal flow stays accessible for the founder
          // demo flow at /demo, just not from the billing page.
          <a
            href={`mailto:hello@lenavio.com?subject=${encodeURIComponent(
              `Enterprise inquiry — ${plan.name || plan.display_name || "Lenavio"}`,
            )}&body=${encodeURIComponent(
              "Hi Lenavio team,\n\nI'd like to learn more about the Enterprise plan for my company.\n\nCompany name:\nApprox. headcount:\nWhat caught your eye:\n\nThanks,\n",
            )}`}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-accent bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent/90"
          >
            <Sparkles className="h-4 w-4" aria-hidden />
            Contact sales
          </a>
        ) : (
          <Button
            variant={current ? "secondary" : "primary"}
            size="md"
            disabled={disabled || !canManage || current}
            onClick={() => onSelect(plan)}
            isLoading={working}
            className="w-full"
            title={!canManage ? "Only an Owner/Super-Admin can change the plan" : undefined}
          >
            {current ? "Active" : (
              <>
                <Sparkles className="h-4 w-4" />
                Upgrade
              </>
            )}
          </Button>
        )}
      </div>
    </Card>
  );
}

export function BillingSettings() {
  const { can } = usePermissions();
  const { addToast } = useToast();
  const { user } = useAuth();

  const canManage = can("manage_billing");

  const [plans, setPlans] = useState<PlanInfo[] | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [workingKey, setWorkingKey] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  // Contact-sales modal state. The org name is fetched lazily on first
  // open so we don't pay for it on every billing-page mount.
  const [contactPlan, setContactPlan] = useState<PlanInfo | null>(null);
  const [contactNotes, setContactNotes] = useState("");
  const [contactSending, setContactSending] = useState(false);
  const [orgName, setOrgName] = useState<string | null>(null);
  const employeeCount = subscription?.seats_used ?? 0;

  const load = useCallback(async () => {
    setLoading(true);
    // Promise.allSettled so a single slow/failing endpoint doesn't hang
    // the entire page in the skeleton state (user reports the Billing
    // tab occasionally "freezes" when one leg stalls).
    const [plansRes, subRes] = await Promise.allSettled([
      listPlans(),
      getSubscription(),
    ]);

    let anyFailed = false;
    if (plansRes.status === "fulfilled") {
      setPlans(plansRes.value);
    } else {
      anyFailed = true;
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error("BillingSettings: listPlans failed", plansRes.reason);
      }
      setPlans([]);
    }
    if (subRes.status === "fulfilled") {
      setSubscription(subRes.value);
    } else {
      anyFailed = true;
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error("BillingSettings: getSubscription failed", subRes.reason);
      }
    }

    if (anyFailed) {
      addToast("error", "Some billing details couldn't be loaded. Retry if needed.");
    }
    setLoading(false);
  }, [addToast]);

  useEffect(() => {
    load();
  }, [load]);

  // If the user just returned from Stripe checkout with ?billing=success, surface it
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("billing");
    if (flag === "success") {
      addToast(
        "success",
        "Subscription update received — changes may take a minute to reflect here"
      );
      params.delete("billing");
      const next = params.toString();
      const url = window.location.pathname + (next ? `?${next}` : "");
      window.history.replaceState({}, "", url);
    } else if (flag === "cancelled") {
      addToast("info", "Checkout cancelled. No changes were made.");
      params.delete("billing");
      const next = params.toString();
      const url = window.location.pathname + (next ? `?${next}` : "");
      window.history.replaceState({}, "", url);
    }
  }, [addToast]);

  const handleUpgrade = async (plan: PlanInfo) => {
    // Dev-only diagnostic trail. Production builds strip these via
    // ``import.meta.env.DEV`` (Vite injects ``false`` and bundlers
    // tree-shake the branch). Keeps useful debug output during local
    // work without leaking plan internals or response URLs into
    // production console / DevTools where they'd be visible to anyone
    // shoulder-surfing or screen-sharing.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log("CHECKOUT CLICKED", {
        planKey: plan?.key,
        planName: plan?.name,
        purchasable: plan?.purchasable,
        canManage,
      });
    }

    if (!canManage) {
      return;
    }
    if (!plan?.key) {
      addToast("error", "Plan information is missing. Refresh and try again.");
      return;
    }

    setWorkingKey(plan.key);
    try {
      const resp = await createCheckout(plan.key);
      // Prefer ``checkout_url`` (backend-native), fall back to the
      // legacy ``url`` alias. Either one is a Stripe-hosted URL.
      const redirectUrl = resp.checkout_url || resp.url;
      if (!redirectUrl) {
        throw new Error("Checkout response missing checkout_url/url");
      }
      window.location.href = redirectUrl;
    } catch (err: unknown) {
      // Log shape only in dev — production users don't benefit from
      // the verbose error envelope, and it sometimes contains the
      // backend's response detail which can include implementation
      // hints we don't want fanned out.
      const ax = err as {
        message?: string;
        code?: string;
        config?: { url?: string; method?: string; baseURL?: string };
        response?: { status?: number; data?: { detail?: unknown } };
      };
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error("BillingSettings: checkout failed", {
          message: ax?.message,
          code: ax?.code,
          status: ax?.response?.status,
        });
      }

      // Extract the most useful human-readable message we can. Previously
      // this fell through to a generic "Could not start checkout" whenever
      // ``detail`` wasn't a string or list — but FastAPI sometimes returns
      // dict-shaped detail (e.g. ``{error: "...", message: "..."}``) and
      // some failures have no response at all. Cover every shape.
      const detail = ax?.response?.data?.detail;
      let message = "Could not start checkout";
      if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail)) {
        const first = detail[0] as { msg?: string } | undefined;
        if (first?.msg) message = first.msg;
      } else if (detail && typeof detail === "object") {
        const d = detail as { message?: string; error?: string; detail?: string };
        message = d.message || d.error || d.detail || JSON.stringify(detail);
      } else if (ax?.code === "ERR_NETWORK" || ax?.message === "Network Error") {
        message = "Network error reaching the billing API. Check CORS / backend availability.";
      } else if (ax?.response?.status) {
        // Response exists but detail was empty — expose the status so the
        // user (or us reading their screenshot) can at least tell what
        // tier of failure this was.
        message = `Checkout failed (HTTP ${ax.response.status})`;
      } else if (ax?.message && !ax?.response) {
        // Client-side throw (e.g. our own "missing checkout_url" above).
        message = ax.message;
      }
      addToast("error", message);
      setWorkingKey(null);
    }
  };

  const handleOpenContact = useCallback(async (plan: PlanInfo) => {
    setContactPlan(plan);
    setContactNotes("");
    // Lazy-load org name on first open. If we already have it, skip the
    // round-trip — org names don't change often enough to bother
    // refetching per modal open.
    if (orgName === null) {
      try {
        const org = await getCurrentOrg();
        setOrgName(org.name);
      } catch {
        // Fall through with a generic placeholder so the form still works.
        setOrgName("our team");
      }
    }
  }, [orgName]);

  const handleCloseContact = useCallback(() => {
    if (contactSending) return;
    setContactPlan(null);
    setContactNotes("");
  }, [contactSending]);

  const handleSubmitContact = async () => {
    if (!contactPlan || !user) return;

    const planLabel = contactPlan.name || contactPlan.key;
    const orgLabel = orgName || "our team";
    const notes = contactNotes.trim();
    const baseMessage = `${planLabel} plan inquiry — ${orgLabel}, ${employeeCount} employee${employeeCount === 1 ? "" : "s"}.`;
    const fullMessage = notes ? `${baseMessage} ${notes}` : baseMessage;

    setContactSending(true);
    try {
      // The /public/demo-request endpoint is unauthenticated by design
      // (it's hit from the marketing site too) — but our auth header
      // tagging along is harmless: the backend ignores it for this route.
      await client.post("/public/demo-request", {
        email: user.email,
        first_name: user.first_name || "there",
        last_name: user.last_name || undefined,
        company: orgLabel,
        team_size: `${employeeCount}`,
        message: fullMessage,
      });
      addToast("success", "We'll be in touch within 24 hours.");
      setContactPlan(null);
      setContactNotes("");
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })
        ?.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : "Could not send your request. Please try again.";
      addToast("error", message);
    } finally {
      setContactSending(false);
    }
  };

  const handlePortal = async () => {
    if (!canManage) return;
    setPortalLoading(true);
    try {
      const { url } = await createPortal();
      window.location.href = url;
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
        "Could not open billing portal";
      addToast("error", message);
      setPortalLoading(false);
    }
  };

  const seatsLabel = useMemo(() => {
    if (!subscription) return "—";
    const limit = subscription.seat_limit;
    if (limit === 0) return `${subscription.seats_used} / Unlimited`;
    return `${subscription.seats_used} / ${limit}`;
  }, [subscription]);

  const seatPct = useMemo(() => {
    if (!subscription || subscription.seat_limit === 0) return 0;
    return Math.min(
      100,
      Math.round((subscription.seats_used / subscription.seat_limit) * 100)
    );
  }, [subscription]);

  const currentPlanKey = subscription?.plan?.toLowerCase() ?? "free";
  const hasSubscription = Boolean(subscription?.stripe_subscription_id);
  const trialExpired =
    (subscription?.status ?? "").toLowerCase() === "trial_expired";

  // Highlight Starter (the ``starter`` tier) as the recommended
  // restart point for orgs whose trial just ended. Enterprise is the
  // wrong default — it's overkill for most teams that didn't decide
  // to buy during the trial. Starter is the lowest-friction paid
  // tier that unlocks the platform again.
  const recommendedPlanKey = trialExpired ? "starter" : null;

  // Try to land the upgrade CTA directly on the recommended plan card
  // when one is selected. Falls back to the section anchor otherwise.
  const handleUpgradeNow = () => {
    const target = document.getElementById("plans-grid");
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="space-y-6">
      {!canManage && (
        <Card className="border-accent/40 bg-accent/5">
          <p className="text-sm text-text-primary">
            You can view the organization's billing status here. Only a{" "}
            <strong>Super Admin</strong> can change plans or open the Stripe portal.
          </p>
        </Card>
      )}

      {trialExpired && (
        <Card className="border-warning/50 bg-warning/5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <AlertTriangle
                className="h-5 w-5 shrink-0 text-warning mt-0.5"
                aria-hidden
              />
              <div>
                <p className="text-sm font-semibold text-text-primary">
                  Your trial has ended
                </p>
                <p className="mt-1 text-sm text-text-muted">
                  Choose a plan to keep using Lenavio. Your data is safe —
                  pick a plan to pick up where you left off.
                </p>
              </div>
            </div>
            {canManage && (
              <Button
                variant="primary"
                size="md"
                onClick={handleUpgradeNow}
                className="shrink-0"
              >
                <Sparkles className="h-4 w-4" />
                Upgrade
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* AI Credits — read-only for HR_ADMIN, full purchase flow
          for SUPER_ADMIN, hidden for MANAGER/EMPLOYEE. Section
          enforces its own role gating; sitting above the
          subscription card so admins see balance + buy options
          before scrolling to plan controls. */}
      <AiCreditsSection />

      <Card>
        <CardHeader>
          <CardTitle>Current subscription</CardTitle>
          {hasSubscription && canManage && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handlePortal}
              isLoading={portalLoading}
            >
              <CreditCard className="h-4 w-4" />
              Manage billing
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          )}
        </CardHeader>
        {loading || !subscription ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-muted">Plan</p>
              <p className="mt-1 text-lg font-semibold font-heading capitalize text-text-primary">
                {subscription.plan}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-text-muted">Status</p>
              <p className="mt-1">
                <Badge variant={statusBadgeVariant(subscription.status)}>
                  {statusLabel(subscription.status)}
                </Badge>
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-text-muted">Seats</p>
              <p className="mt-1 text-lg font-semibold font-heading text-text-primary">
                {seatsLabel}
              </p>
              {subscription.seat_limit > 0 && (
                <div className="mt-2 h-1.5 w-full rounded-full bg-border/60">
                  <div
                    className={cn(
                      "h-1.5 rounded-full transition-all",
                      seatPct >= 100
                        ? "bg-danger"
                        : seatPct >= 80
                          ? "bg-warning"
                          : "bg-accent"
                    )}
                    style={{ width: `${seatPct}%` }}
                  />
                </div>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-text-muted">
                {subscription.trial_ends_at ? "Trial ends" : "Renews"}
              </p>
              <p className="mt-1 text-lg font-semibold font-heading text-text-primary">
                {formatDate(
                  subscription.trial_ends_at ?? subscription.current_period_end
                )}
              </p>
            </div>
          </div>
        )}

        {/* Per-seat billing math — only render when the active plan
            has a per-seat price (Starter $12 / Professional $22).
            Free + Enterprise hide the line. Trialing shows it as a
            preview so the customer knows what they'd pay post-trial. */}
        {subscription?.per_seat_price_usd != null &&
          subscription.seats_used > 0 && (
            <div className="mt-4 rounded-md border border-dashed border-border bg-surface-muted/40 px-4 py-3">
              <p className="text-sm">
                You have{" "}
                <strong className="tabular-nums">
                  {subscription.seats_used}
                </strong>{" "}
                active employee
                {subscription.seats_used === 1 ? "" : "s"} ×{" "}
                <strong className="tabular-nums">
                  ${subscription.per_seat_price_usd.toFixed(0)}/employee
                </strong>{" "}
                ={" "}
                <strong className="tabular-nums">
                  $
                  {(
                    subscription.seats_used *
                    subscription.per_seat_price_usd
                  ).toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                  /month
                </strong>
                {(subscription.status ?? "").toLowerCase() === "trialing"
                  ? " — billing starts when your trial ends"
                  : ""}
              </p>
              <p className="mt-1 text-xs text-text-muted">
                Stripe automatically prorates when you add or remove
                employees. Charges adjust on your next invoice.
              </p>
            </div>
          )}
      </Card>

      <div id="plans-grid">
        <h4 className="mb-4 text-sm font-semibold uppercase tracking-wide text-text-muted">
          Plans
        </h4>
        {loading || !plans ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-64" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {plans.map((plan) => (
              <PlanCard
                key={plan.key}
                plan={plan}
                current={plan.key.toLowerCase() === currentPlanKey}
                canManage={canManage}
                disabled={Boolean(workingKey)}
                working={workingKey === plan.key}
                onSelect={handleUpgrade}
                onContactSales={handleOpenContact}
                recommended={
                  recommendedPlanKey != null &&
                  plan.key.toLowerCase() === recommendedPlanKey
                }
              />
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-text-muted">
        Payments are processed securely by Stripe. Lenavio never stores your card details.
      </p>

      <Modal
        isOpen={contactPlan !== null}
        onClose={handleCloseContact}
        title={
          contactPlan
            ? `Contact sales — ${contactPlan.name || contactPlan.key}`
            : "Contact sales"
        }
        description="Tell us a bit about your needs and we'll be in touch within 24 hours."
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-muted">
              Your message
            </label>
            <p className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary">
              {contactPlan
                ? `I'm interested in the ${contactPlan.name || contactPlan.key} plan for ${orgName ?? "our team"} (${employeeCount} employee${employeeCount === 1 ? "" : "s"}).`
                : ""}
            </p>
          </div>

          <div>
            <label
              htmlFor="contact-sales-notes"
              className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-muted"
            >
              Anything else? (optional)
            </label>
            <textarea
              id="contact-sales-notes"
              value={contactNotes}
              onChange={(e) => setContactNotes(e.target.value)}
              placeholder="Specific questions, timeline, integrations you need…"
              rows={4}
              maxLength={1500}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={handleCloseContact}
              disabled={contactSending}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmitContact} isLoading={contactSending}>
              Send request
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
